import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Checkbox,
  CircularProgress,
  Tooltip,
  InputAdornment,
  Divider,
  Menu,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { getDisplayUnits, getUnitDisplayTitle, getUnitListIds, isDirectLot } from '../../utils/userLotUnits';
import { formatUserLotCommercial } from '../../utils/userLotFormUtils';
import ActionMenuItem, { ACTION_MENU_LIST_PROPS, ACTION_MENU_PAPER_PROPS } from '../ActionMenuItem';
import ListsCollapsibleSection from './ListsCollapsibleSection';
import {
  listSearchFieldSx,
  listSearchIconSx,
  detailSectionDividerSx,
  addFlowFooterRowSx,
  addFlowFooterLabelSx,
  addFlowCancelButtonSx,
  addFlowReviewButtonSx,
  addFlowConfirmButtonSx,
  addFlowMapHintBoxSx,
  addFlowRecordCardSx,
  addFlowRecordTitleSx,
  addFlowRecordMetaSx,
  addFlowTabsRowSx,
  getAddFlowTabButtonSx,
  addFlowMapHintIconSx,
  addFlowMapHintTitleSx,
  addFlowMapHintTextSx,
  addFlowMapPickLoadingCardSx,
} from './UserLotListsPanel.styles';

const BG_ELEVATED = '#242424';
const BG_UNITS = '#141414';
const BORDER = '#383838';
const TEXT_PRIMARY = '#D4D4D4';
const TEXT_SECONDARY = '#ABABAB';
const CARD_PAD_X = 12;
const CHEVRON_COL = 24;
const HEADER_GAP = 6;

const addFlowCheckboxSx = {
  width: 22,
  height: 22,
  minWidth: 22,
  p: 0,
  color: 'rgba(255,255,255,0.65)',
  '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#6DA8A7' },
  '& .MuiSvgIcon-root': {
    fontSize: 22,
    width: 22,
    height: 22,
  },
};

const formatUnitMeta = (unit) => formatUserLotCommercial(unit);

function UnitCountChip({ available, total }) {
  if (!available) return null;
  const alreadyInList = Math.max(0, (total || 0) - available);
  const label = alreadyInList > 0
    ? `${alreadyInList} de ${total} unidades ya ${alreadyInList === 1 ? 'está' : 'están'} en la lista`
    : `${available} ${available === 1 ? 'disponible' : 'disponibles'}`;
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 22,
        borderRadius: '4px',
        bgcolor: 'rgba(109,168,167,0.15)',
        color: '#6DA8A7',
        fontSize: '11px',
        fontWeight: 500,
        '& .MuiChip-label': { px: '8px' },
      }}
    />
  );
}

function RecordLotRow({
  lot,
  addableUnits,
  listId,
  parcelInList = false,
  isSelected,
  isPartiallySelected,
  onToggle,
  onLocateLot,
  expandedLotIds,
  onToggleExpand,
  staging,
  onToggleUnit,
}) {
  const units = getDisplayUnits(lot);
  const hasUnits = units.length > 0;
  const isExpanded = expandedLotIds.has(lot.lot_id);
  const direct = isDirectLot(lot);
  const totalUnits = units.length;
  const availableCount = direct
    ? 0
    : listId
      ? units.filter((unit) => !getUnitListIds(unit).includes(listId)).length
      : (addableUnits?.length || 0);
  const alreadyInListCount = Math.max(0, totalUnits - availableCount);
  // Si el lote ya está vinculado (parcela o UF), solo se eligen unidades: sin checkbox de parcela.
  const hideLotCheckbox = parcelInList || alreadyInListCount > 0;
  const contentPadLeft = CARD_PAD_X + CHEVRON_COL + HEADER_GAP;
  // Todas las UF del lote: las ya en lista se muestran deshabilitadas.
  const expandedUnits = direct ? [] : units;

  return (
    <Box sx={{ ...addFlowRecordCardSx, overflow: 'hidden', px: 0, py: 0 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.75,
          px: `${CARD_PAD_X}px`,
          py: '10px',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: `${HEADER_GAP}px` }}>
          <Box
            sx={{
              width: CHEVRON_COL,
              height: CHEVRON_COL,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {hasUnits ? (
              <IconButton
                size="small"
                onClick={(e) => onToggleExpand(e, lot.lot_id)}
                sx={{
                  width: CHEVRON_COL,
                  height: CHEVRON_COL,
                  p: 0,
                  color: TEXT_SECONDARY,
                  // Colapsado → derecha; expandido → abajo (ExpandMore apunta abajo por defecto).
                  transform: isExpanded ? 'none' : 'rotate(-90deg)',
                  transition: 'transform 0.15s ease',
                }}
              >
                <ExpandMoreIcon sx={{ fontSize: 20 }} />
              </IconButton>
            ) : null}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={addFlowRecordTitleSx}>
              Número Catastral: {lot.lot_name || lot.lot_id}
            </Typography>
            <Typography sx={addFlowRecordMetaSx}>
              {[lot.province_name, lot.department_name].filter(Boolean).join(' · ') || '—'}
            </Typography>
            {availableCount > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                <UnitCountChip available={availableCount} total={totalUnits} />
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, pt: 0.25 }}>
          <Tooltip title="Ubicar en el mapa" placement="top" arrow>
            <IconButton
              size="small"
              onClick={() => onLocateLot?.(lot.lot_id, lot)}
              sx={{
                width: 22,
                height: 22,
                p: 0,
                color: TEXT_SECONDARY,
                '&:hover': { color: TEXT_PRIMARY },
              }}
            >
              <MyLocationOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          {!hideLotCheckbox && (
            <Checkbox
              checked={isSelected}
              indeterminate={isPartiallySelected}
              onChange={() => onToggle(lot, addableUnits)}
              size="small"
              icon={<RadioButtonUncheckedIcon />}
              checkedIcon={<CheckCircleIcon />}
              indeterminateIcon={<CheckCircleIcon />}
              sx={addFlowCheckboxSx}
            />
          )}
        </Box>
      </Box>

      {hasUnits && isExpanded && expandedUnits.length > 0 && (
        <Box
          sx={{
            bgcolor: BG_UNITS,
            borderTop: `1px solid ${BORDER}`,
          }}
        >
          {expandedUnits.map((unit, idx) => {
            const meta = formatUnitMeta(unit);
            const isLast = idx === expandedUnits.length - 1;
            const alreadyInList = Boolean(listId && getUnitListIds(unit).includes(listId));
            const isUnitSelected = alreadyInList
              || staging.unitIds.has(unit.id)
              || Boolean(staging.lotUnitPicks[lot.lot_id]?.unitIds?.has(unit.id));
            return (
              <Box
                key={unit.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '10px',
                  pl: `${contentPadLeft}px`,
                  pr: `${CARD_PAD_X}px`,
                  py: '10px',
                  borderBottom: isLast ? 'none' : `1px solid ${BORDER}`,
                  opacity: alreadyInList ? 0.55 : 1,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography
                    sx={{
                      color: alreadyInList ? TEXT_SECONDARY : TEXT_PRIMARY,
                      fontSize: '13px',
                      fontWeight: 500,
                    }}
                  >
                    {getUnitDisplayTitle(unit)}
                  </Typography>
                  {meta ? (
                    <Typography sx={{ color: TEXT_SECONDARY, fontSize: '12px', lineHeight: '16px' }}>
                      {meta}
                    </Typography>
                  ) : null}
                </Box>
                <Checkbox
                  checked={isUnitSelected}
                  disabled={alreadyInList}
                  onChange={() => {
                    if (alreadyInList) return;
                    onToggleUnit(lot, unit, addableUnits);
                  }}
                  size="small"
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={<CheckCircleIcon />}
                  sx={{
                    ...addFlowCheckboxSx,
                    '&.Mui-disabled': {
                      color: 'rgba(109,168,167,0.45)',
                      opacity: 1,
                    },
                  }}
                />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

function ReviewLotCard({
  lot,
  selectedUnits = [],
  addableUnits = [],
  selectedUnitIds = null,
  isExpanded,
  onToggleExpand,
  onLocateLot,
  onRemove,
  onToggleUnit,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const hasUnits = selectedUnitIds
    ? selectedUnitIds.size > 0
    : selectedUnits.length > 0;
  const displayUnits = selectedUnitIds
    ? addableUnits.filter((unit) => selectedUnitIds.has(unit.id))
    : selectedUnits;
  const unitCount = displayUnits.length;
  const locationLabel = [lot?.province_name, lot?.department_name].filter(Boolean).join(' · ') || '—';

  return (
    <Box
      sx={{
        borderRadius: '8px',
        bgcolor: BG_ELEVATED,
        overflow: 'hidden',
        mb: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: `${HEADER_GAP}px`,
          px: `${CARD_PAD_X}px`,
          py: '10px',
          cursor: hasUnits ? 'pointer' : 'default',
        }}
        onClick={() => {
          if (hasUnits && onToggleExpand) onToggleExpand({ stopPropagation() {} }, lot.lot_id);
        }}
      >
        <Box
          sx={{
            width: CHEVRON_COL,
            height: CHEVRON_COL,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {hasUnits ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand?.(e, lot.lot_id);
              }}
              sx={{
                width: CHEVRON_COL,
                height: CHEVRON_COL,
                p: 0,
                color: TEXT_SECONDARY,
                transform: isExpanded ? 'none' : 'rotate(-90deg)',
                transition: 'transform 0.15s ease',
              }}
            >
              <ExpandMoreIcon sx={{ fontSize: 20 }} />
            </IconButton>
          ) : null}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <Typography sx={addFlowRecordTitleSx}>
            Número Catastral: {lot?.lot_name || lot?.lot_id}
          </Typography>
          <Typography sx={addFlowRecordMetaSx}>{locationLabel}</Typography>
          {unitCount > 0 && (
            <Chip
              size="small"
              label={`${unitCount} Unidad${unitCount !== 1 ? 'es' : ''}`}
              sx={{
                alignSelf: 'flex-start',
                height: 22,
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: 500,
                bgcolor: 'transparent',
                color: TEXT_PRIMARY,
                border: `1px solid ${BORDER}`,
                '& .MuiChip-label': { px: '8px' },
              }}
            />
          )}
        </Box>

        <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
          <Tooltip title="Más acciones" placement="top" arrow>
            <IconButton
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{
                color: TEXT_SECONDARY,
                p: 0.5,
                '&:hover': { color: '#F9FAFB', bgcolor: 'rgba(255,255,255,0.06)' },
              }}
            >
              <MoreVertIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={ACTION_MENU_PAPER_PROPS}
            MenuListProps={ACTION_MENU_LIST_PROPS}
          >
            <ActionMenuItem
              onClick={() => {
                setMenuAnchor(null);
                onLocateLot?.(lot.lot_id, lot);
              }}
              icon={<MyLocationOutlinedIcon sx={{ fontSize: 20 }} />}
              label="Ubicar en el mapa"
            />
            <ActionMenuItem
              onClick={() => {
                setMenuAnchor(null);
                onRemove?.();
              }}
              icon={<DeleteOutlineIcon sx={{ fontSize: 20 }} />}
              label="Quitar de la selección"
            />
          </Menu>
        </Box>
      </Box>

      {hasUnits && isExpanded && displayUnits.length > 0 && (
        <Box sx={{ bgcolor: BG_UNITS, borderTop: `1px solid ${BORDER}` }}>
          {displayUnits.map((unit, idx) => {
            const meta = formatUnitMeta(unit);
            const isLast = idx === displayUnits.length - 1;
            return (
              <Box
                key={unit.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '10px',
                  pl: `${CARD_PAD_X + CHEVRON_COL + HEADER_GAP}px`,
                  pr: `${CARD_PAD_X}px`,
                  py: '10px',
                  borderBottom: isLast ? 'none' : `1px solid ${BORDER}`,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px', fontWeight: 500 }}>
                    {getUnitDisplayTitle(unit)}
                  </Typography>
                  {meta ? (
                    <Typography sx={{ color: TEXT_SECONDARY, fontSize: '12px', lineHeight: '16px' }}>
                      {meta}
                    </Typography>
                  ) : null}
                </Box>
                {onToggleUnit ? (
                  <Checkbox
                    checked={selectedUnitIds ? selectedUnitIds.has(unit.id) : true}
                    onChange={() => onToggleUnit(lot.lot_id, unit.id, addableUnits)}
                    size="small"
                    icon={<RadioButtonUncheckedIcon />}
                    checkedIcon={<CheckCircleIcon />}
                    sx={addFlowCheckboxSx}
                  />
                ) : null}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

function ReviewSourceSection({
  title,
  bucket,
  expanded,
  onToggle,
  expandedLotIds,
  onToggleExpand,
  onLocateLot,
  onRemoveReviewDirectLot,
  onRemoveReviewLotPick,
  onToggleReviewUnit,
}) {
  if (!bucket?.count) return null;

  return (
    <ListsCollapsibleSection
      title={title}
      count={bucket.count}
      expanded={expanded}
      onToggle={onToggle}
      hasItems
      bodySx={{ maxHeight: 'none' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {bucket.directLots.map((lot) => (
          <ReviewLotCard
            key={`direct-${lot.lot_id}`}
            lot={lot}
            isExpanded={false}
            onLocateLot={onLocateLot}
            onRemove={() => onRemoveReviewDirectLot(lot.lot_id)}
          />
        ))}
        {bucket.lotPicks.map(({ lot, addableUnits, selectedUnitIds }) => (
          <ReviewLotCard
            key={`pick-${lot.lot_id}`}
            lot={lot}
            addableUnits={addableUnits}
            selectedUnitIds={selectedUnitIds}
            isExpanded={expandedLotIds.has(lot.lot_id)}
            onToggleExpand={onToggleExpand}
            onLocateLot={onLocateLot}
            onRemove={() => onRemoveReviewLotPick(lot.lot_id)}
            onToggleUnit={onToggleReviewUnit}
          />
        ))}
        {bucket.standaloneUnits.map(({ lot, unit }) => (
          <ReviewLotCard
            key={`unit-${unit.id}`}
            lot={lot || { lot_id: unit.lot_id, lot_name: unit.lot_name }}
            selectedUnits={[unit]}
            isExpanded={expandedLotIds.has(lot?.lot_id || unit.lot_id)}
            onToggleExpand={onToggleExpand}
            onLocateLot={onLocateLot}
            onRemove={() => onToggleReviewUnit(null, unit.id)}
          />
        ))}
      </Box>
    </ListsCollapsibleSection>
  );
}

/** El lote clickeado en el mapa puede tardar unos segundos en resolverse. */
function MapPickLoadingCard() {
  return (
    <Box sx={addFlowMapPickLoadingCardSx}>
      <Box
        sx={{
          width: CHEVRON_COL,
          height: CHEVRON_COL,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={16} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={addFlowRecordTitleSx}>
          Agregando la información del lote…
        </Typography>
        <Typography sx={addFlowRecordMetaSx}>
          Puede demorar unos segundos
        </Typography>
      </Box>
    </Box>
  );
}

export default function AddLotsToListFlow({
  section = 'all',
  step,
  sourceTab,
  onSourceTabChange,
  addSearch,
  onAddSearchChange,
  loadingAllLots,
  recordsLots,
  listId,
  parcelInListIds,
  staging,
  expandedLotIds,
  onToggleExpand,
  onToggleRecordLot,
  onToggleRecordUnit,
  onLocateLot,
  isRecordLotSelected,
  isRecordLotPartial,
  stagedCount,
  stagedMapLots,
  isResolvingMapPick = false,
  reviewGroups,
  onToggleReviewUnit,
  onRemoveReviewDirectLot,
  onRemoveReviewLotPick,
  isCommitting,
  onCancel,
  onGoToReview,
  onBackFromReview,
  onConfirm,
}) {
  const isPick = step === 'pick';
  const isReview = step === 'review';
  const [reviewRecordsExpanded, setReviewRecordsExpanded] = useState(true);
  const [reviewMapExpanded, setReviewMapExpanded] = useState(true);

  const topContent = isPick ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
      <Box sx={addFlowTabsRowSx}>
        <Button
          size="small"
          startIcon={<DashboardOutlinedIcon />}
          onClick={() => onSourceTabChange('records')}
          sx={getAddFlowTabButtonSx(sourceTab === 'records')}
        >
          Desde registros
        </Button>
        <Button
          size="small"
          startIcon={<LocationOnOutlinedIcon />}
          onClick={() => onSourceTabChange('map')}
          sx={getAddFlowTabButtonSx(sourceTab === 'map')}
        >
          Desde el mapa
        </Button>
      </Box>

      {sourceTab === 'records' && (
        <TextField
          size="small"
          placeholder="Buscar por ubicación, descripción o número catastral"
          value={addSearch}
          onChange={(e) => onAddSearchChange(e.target.value)}
          sx={{ ...listSearchFieldSx, width: '100%' }}
          inputProps={{ maxLength: 80 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={listSearchIconSx} />
              </InputAdornment>
            ),
          }}
        />
      )}
    </Box>
  ) : null;

  const mainContent = (
    <>
      {isPick && sourceTab === 'map' && (
        stagedMapLots.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
            {isResolvingMapPick && <MapPickLoadingCard />}
            {stagedMapLots.map(({ lot, addableUnits }) => (
              <RecordLotRow
                key={lot.lot_id}
                lot={lot}
                addableUnits={addableUnits}
                listId={listId}
                parcelInList={parcelInListIds?.has?.(lot.lot_id)}
                isSelected={isRecordLotSelected(lot, addableUnits)}
                isPartiallySelected={isRecordLotPartial(lot, addableUnits)}
                onToggle={onToggleRecordLot}
                onLocateLot={onLocateLot}
                expandedLotIds={expandedLotIds}
                onToggleExpand={onToggleExpand}
                staging={staging}
                onToggleUnit={onToggleRecordUnit}
              />
            ))}
          </Box>
        ) : isResolvingMapPick ? (
          <Box sx={addFlowMapHintBoxSx}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography sx={addFlowMapHintTitleSx}>
              Agregando la información del lote…
            </Typography>
            <Typography sx={addFlowMapHintTextSx}>
              Puede demorar unos segundos la primera vez.
            </Typography>
          </Box>
        ) : (
          <Box sx={addFlowMapHintBoxSx}>
            <AdsClickIcon sx={addFlowMapHintIconSx} />
            <Typography sx={addFlowMapHintTitleSx}>
              Seleccioná los lotes directamente desde el mapa.
            </Typography>
            <Typography sx={addFlowMapHintTextSx}>
              Se agrega el lote por defecto. Si ya está en la lista, podés sumar solo las unidades pendientes expandiendo la card.
            </Typography>
          </Box>
        )
      )}

      {isPick && sourceTab === 'records' && (
        loadingAllLots ? null : recordsLots.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography sx={{ color: TEXT_PRIMARY, fontSize: '14px', fontWeight: 600 }}>
              No hay lotes ni unidades para agregar
            </Typography>
            <Typography sx={{ color: TEXT_SECONDARY, fontSize: '12px', mt: 0.5 }}>
              Todo lo disponible ya está en esta lista, o no hay resultados para la búsqueda.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
            {recordsLots.slice(0, 50).map(({ lot, addableUnits }) => (
              <RecordLotRow
                key={lot.lot_id}
                lot={lot}
                addableUnits={addableUnits}
                listId={listId}
                parcelInList={parcelInListIds?.has?.(lot.lot_id)}
                isSelected={isRecordLotSelected(lot, addableUnits)}
                isPartiallySelected={isRecordLotPartial(lot, addableUnits)}
                onToggle={onToggleRecordLot}
                onLocateLot={onLocateLot}
                expandedLotIds={expandedLotIds}
                onToggleExpand={onToggleExpand}
                staging={staging}
                onToggleUnit={onToggleRecordUnit}
              />
            ))}
            {recordsLots.length > 50 && (
              <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '11.5px', mt: 0.5 }}>
                Mostrando 50 resultados. Refiná la búsqueda para ver más.
              </Typography>
            )}
          </Box>
        )
      )}

      {isReview && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5, width: '100%' }}>
          <ReviewSourceSection
            title="REGISTROS"
            bucket={reviewGroups.records}
            expanded={reviewRecordsExpanded}
            onToggle={() => setReviewRecordsExpanded((prev) => !prev)}
            expandedLotIds={expandedLotIds}
            onToggleExpand={onToggleExpand}
            onLocateLot={onLocateLot}
            onRemoveReviewDirectLot={onRemoveReviewDirectLot}
            onRemoveReviewLotPick={onRemoveReviewLotPick}
            onToggleReviewUnit={onToggleReviewUnit}
          />
          <ReviewSourceSection
            title="MAPA"
            bucket={reviewGroups.map}
            expanded={reviewMapExpanded}
            onToggle={() => setReviewMapExpanded((prev) => !prev)}
            expandedLotIds={expandedLotIds}
            onToggleExpand={onToggleExpand}
            onLocateLot={onLocateLot}
            onRemoveReviewDirectLot={onRemoveReviewDirectLot}
            onRemoveReviewLotPick={onRemoveReviewLotPick}
            onToggleReviewUnit={onToggleReviewUnit}
          />
          {stagedCount === 0 && (
            <Typography sx={{ color: TEXT_SECONDARY, fontSize: '13px', textAlign: 'center', py: 2 }}>
              No hay ítems seleccionados
            </Typography>
          )}
        </Box>
      )}
    </>
  );

  const footerContent = (isPick || isReview) ? (
    <Box sx={addFlowFooterRowSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
        {stagedCount > 0 && (
          <CheckCircleIcon sx={{ width: 18, height: 18, color: '#6DA8A7', flexShrink: 0 }} />
        )}
        <Typography sx={addFlowFooterLabelSx}>
          {stagedCount === 0
            ? 'Ningún ítem seleccionado'
            : `${stagedCount} agregado${stagedCount === 1 ? '' : 's'} a la lista`}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={isReview ? onBackFromReview : onCancel}
          disabled={isCommitting}
          sx={addFlowCancelButtonSx}
        >
          Cancelar
        </Button>
        {isPick ? (
          <Button
            variant="contained"
            size="small"
            onClick={onGoToReview}
            disabled={stagedCount === 0}
            sx={addFlowReviewButtonSx}
          >
            Revisar
          </Button>
        ) : (
          <Button
            variant="contained"
            size="small"
            onClick={onConfirm}
            disabled={isCommitting || stagedCount === 0 || reviewGroups.hasInvalidLotPicks}
            startIcon={
              isCommitting ? (
                <CircularProgress size={14} sx={{ color: 'inherit' }} />
              ) : (
                <AddIcon sx={{ fontSize: 18 }} />
              )
            }
            sx={addFlowConfirmButtonSx}
          >
            Agregar a lista
          </Button>
        )}
      </Box>
    </Box>
  ) : null;

  if (section === 'top') return topContent;
  if (section === 'main') return mainContent;
  if (section === 'footer') return footerContent;

  return (
    <>
      {topContent}
      {mainContent}
      {footerContent && (
        <>
          <Divider sx={{ ...detailSectionDividerSx, mx: -3, mt: isReview ? 1 : 0 }} />
          {footerContent}
        </>
      )}
    </>
  );
}
